import * as yup from 'yup';
import { Trash2 } from 'react-feather';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { DiscountType, GetDiscountQuery as GetDiscount, ProductType } from '__generated__/graphql';
import { TranslatableInput } from '../../../Translations/Inputs';
import { getServerErrors } from '../../../../common/helpers';
import { translationsForAttribute } from '../../../Translations/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import DateRangeForm, { dateRangeObject } from '../../../Common/DateRangeForm';
import DiscountValueInput from './DiscountValueInput';
import EditDiscountMutation from './EditDiscountMutation';
import ProductSelectionForm from '../../../Common/ProductSelectionForm';
import TicketSelectionForm from '../../../Common/TicketSelectionForm';
import UI from '../../../../common/UI';
import useIntegerInput from '../../../../common/useIntegerInput';
import useMutation from '../../../../api/useMutation';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useScroll from '../../../../common/useScroll';
import useValidation from '../../../../common/useValidation';

type Discount = GetDiscount['discount'];
type Event = GetDiscount['event'];

const discountFormSchema = yup.object().shape({
  title: yup.string().required(),
  ...dateRangeObject(false, false),
});

export interface DiscountLevelData {
  quantity: number;
  value: number;
}

export interface EditDiscountFormProps {
  discount: Discount;
  event: Event;
}

const EditDiscountForm = ({ discount, event }: EditDiscountFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const project = useProject();
  const integerInput = useIntegerInput();
  const { scrollToIfInvisible } = useScroll();

  const getValues = (discount: Discount) => ({
    id: discount.id,
    type: discount.type,
    title: discount.title,
    title_translations: translationsForAttribute(discount.translations, 'title'),
    start_date: discount.start_date,
    end_date: discount.end_date,
    tickets: discount.tickets.map(({ id }) => ({ id })),
    products: discount.products.map(({ id }) => ({ id })),
    levels: [
      ...discount.levels.map(({ quantity, value }) => ({
        quantity,
        value,
      })),
      ...discount.levels.length === 0 ? [{ quantity: 1, value: null }] : [],
      { quantity: null, value: null },
    ],
  });

  const form = useForm({
    values: getValues(discount),
  });

  useNavigationPrompt(form.changed());

  const levelContainsValue = (level: DiscountLevelData) => (
    typeof level.quantity === 'number' || typeof level.value === 'number'
  );

  const [editDiscount, { error, loading }] = useMutation(EditDiscountMutation, {
    variables: {
      input: {
        ...form.values,
        levels: form.values.levels.slice(0, form.values.levels.length - 1).map((level) => ({
          ...level,
          value: level.value || 0,
        })),
      },
    },
    onCompleted: ({ editDiscount: discount }) => {
      notifier.success(t('notifications.discount_saved'));

      form.reset(getValues(discount));
    },
    onError: () => {
      scrollToIfInvisible(`DiscountCard_${discount.id}`);
    },
  });

  const { tickets } = event;
  const products = event.products
    .filter((product) => product.type === ProductType.item || product.type === ProductType.fee);

  const setType = (type: DiscountType) => {
    form.set((values) => ({
      ...values,
      type,
      levels: values.levels.map((level) => {
        if (level.value && type !== values.type) {
          if (type === DiscountType.absolute) {
            level.value *= 100;
          } else if (type === DiscountType.percentage) {
            level.value = Math.min(Math.round(level.value / 100), 100);
          }
        }

        return { ...level };
      }),
    }));
  };

  const lastLevel = form.values.levels[form.values.levels.length - 1];
  const levelKeys = useRef(form.values.levels.map((value, index) => index));

  const deleteLevel = (index: number) => {
    form.delete('levels', index);
    levelKeys.current.splice(index, 1);
  };

  useEffect(() => {
    if (lastLevel && levelContainsValue(lastLevel)) {
      form.set('levels', (levels: DiscountLevelData[]) => [
        ...levels,
        { quantity: null, value: null },
      ]);
      levelKeys.current.push(Math.max(0, ...levelKeys.current) + 1);
    }
  }, [form.values.levels, lastLevel?.quantity, lastLevel?.value]);

  const { valid, errors } = useValidation({
    schema: discountFormSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Div id={`EditDiscountForm_${discount.id}`}>
      <UI.Form onSubmit={() => editDiscount()}>
        <UI.FormGrid>
          <UI.ServerErrors error={error} />

          <UI.Div>
            <UI.Legend sc={{ mb: 1 }}>
              {t('basic_info')}
            </UI.Legend>
            <UI.InputDescription>
              {t('discount_basic_info')}
            </UI.InputDescription>
          </UI.Div>

          <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
            <UI.InputLabel htmlFor="DiscountTitle">
              {t('discount_title')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <TranslatableInput
              form={form}
              name="title"
              defaultLocale={discount.locale}
              title={t('discount_title')}
              placeholder={t('discount_title_placeholder')}
              id="DiscountTitle"
            />
            <UI.ErrorMessages attribute={t('discount_title')} errors={errors.title} />
          </UI.InputGroup>

          <UI.InputGroup sc={{ valid: !errors.type, touched: form.touched.type }}>
            <UI.InputLabel>
              {t('discount_type')}
            </UI.InputLabel>
            <UI.Radio
              onChange={() => setType(DiscountType.absolute)}
              checked={form.values.type === DiscountType.absolute}
            >
              {t('absolute_amount')}
            </UI.Radio>
            <UI.Radio
              onChange={() => setType(DiscountType.percentage)}
              checked={form.values.type === DiscountType.percentage}
            >
              {t('percentage_amount')}
            </UI.Radio>
            <UI.ErrorMessages
              attribute={t('discount_type')}
              errors={errors.type}
            />
          </UI.InputGroup>

          <UI.FormGrid sc={{ gutter: 0.5 }}>
            <UI.FormGrid sc={{ columns: ['1fr 1fr 32px', '2fr 2fr 3fr'] }}>
              <UI.Div>
                <UI.InputLabel htmlFor="DiscountQuantity_0" sc={{ mb: 0 }}>
                  {t('discount_quantity_label')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
              </UI.Div>
              <UI.Div>
                <UI.InputLabel htmlFor="DiscountValue_levels0Value" sc={{ mb: 0 }}>
                  {t('discount_value_label')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
              </UI.Div>
            </UI.FormGrid>
            {form.values.levels.map((level, index) => (
              <UI.FormGrid
                sc={{
                  columns: ['1fr 1fr 32px', '2fr 2fr 3fr'],
                  muted: !levelContainsValue(level),
                }}
                key={levelKeys.current[index]}
              >
                <UI.InputGroup
                  sc={{
                    valid: !errors[`levels.${index}.quantity`],
                    touched: form.getTouched(`levels.${index}.quantity`),
                  }}
                >
                  <UI.InputWrapper>
                    <UI.DebouncedInput
                      {...form.text(`levels.${index}.quantity`, { inputHandler: integerInput() })}
                      format={integerInput().format}
                      maxLength={4}
                      inputMode="numeric"
                      match={/[\d]/g}
                      id={`DiscountQuantity_${index}`}
                    />
                    <UI.Span>
                      {t('items', { count: level.quantity })}
                    </UI.Span>
                  </UI.InputWrapper>
                  <UI.ErrorMessages
                    attribute={t('min_quantity')}
                    errors={errors[`levels.${index}.quantity`]}
                  />
                </UI.InputGroup>
                <UI.InputGroup
                  sc={{
                    valid: !errors[`levels.${index}.value`],
                    touched: form.getTouched(`levels.${index}.value`),
                  }}
                >
                  <DiscountValueInput form={form} prefix={`levels.${index}`} />
                  <UI.ErrorMessages
                    attribute={t('discount')}
                    errors={errors[`levels.${index}.value`]}
                  />
                </UI.InputGroup>
                {form.values.levels.length > 2 && index + 1 < form.values.levels.length && (
                  <UI.Div sc={{ pt: 1 }}>
                    <UI.Button
                      sc={{ blank: true, size: 'sm', color: 'gray.400' }}
                      onClick={() => deleteLevel(index)}
                    >
                      <UI.Icon>
                        <Trash2 />
                      </UI.Icon>
                    </UI.Button>
                  </UI.Div>
                )}
              </UI.FormGrid>
            ))}
          </UI.FormGrid>

          <UI.HR />

          <UI.Div>
            <UI.Legend sc={{ mb: 1 }}>
              {t('tickets_products')}
            </UI.Legend>
            <UI.InputDescription>
              {t('discount_tickets_products_info')}
            </UI.InputDescription>
          </UI.Div>

          <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
            <TicketSelectionForm
              form={form}
              tickets={tickets}
              errors={errors}
              event={event}
              isDisabled={(ticket) => ticket.discounts.filter((item) => item.id !== discount.id).length > 0}
            />

            <ProductSelectionForm
              form={form}
              products={products}
              errors={errors}
              event={event}
              isDisabled={(product) => product.discounts.filter((item) => item.id !== discount.id).length > 0}
            />
          </UI.FormGrid>

          {discount.active && form.values.tickets.length === 0 && form.values.products.length === 0 && (
            <UI.Warning>
              {(t('discount_no_tickets_or_products_warning'))}
            </UI.Warning>
          )}

          <UI.HR />

          <UI.Div>
            <UI.Legend sc={{ mb: 1 }}>
              {t('availability')}
            </UI.Legend>
            <UI.InputDescription>
              {t('discount_start_and_end_date')}
            </UI.InputDescription>
          </UI.Div>

          <DateRangeForm form={form} errors={errors} />

          <UI.HR />

          <UI.FormButtons
            form={form}
            submitLabel={t('save_discount')}
            closeTo={`/${project.id}/events/${event.id}/discounts`}
            loading={loading}
            valid={valid}
          />
        </UI.FormGrid>
      </UI.Form>
    </UI.Div>
  );
};

export default EditDiscountForm;
